import React, { useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Popover,
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import { downloadCampaignData, fetchCampaign, fetchCampaignNames, fetchOwnerNames, loadCampaigns, manageCampaignPaginationCookie } from "../../../api_calls/campaignRequests";
import { fetchCompanyHierarchy } from "../../../api_calls/companyRequests";
import { LISTING_RELOAD, listingTableIsLoading } from "../../../api_calls/listingReload";
import {
  HtmlTooltipDisabled,
  ThumbnailText,
  ThumbnailWithText,
} from "../../../components/commonComponents/StyledComponents/styled";
import ProgressBar from "../../../components/commonComponents/Progress/ProgressBar";
import {
  checkUserDetailsFromCookie,
  checkUserPermissions,
  checkUserRole,
  pageAccessAllowed,
} from "../../../api_calls/userManagement";
import { useNavigate } from "react-router-dom";
import AddNewMarketingActivity from "../ModalContent/AddMarketingActivity";
import AddNewCampaign from "../ModalContent/AddCampaign";
import { fetchRequiredDetailsBeforePopup } from "../../../api_calls/marketingActivities";
import MarketingActivitiesList from "./MarketingActivitiesList";
import { resetCampaignSheetData } from "../../../api_calls/campaignSheet";
import { resetMarketingActivitiesData } from "../../../api_calls/marketingActivities";
import ReportSectionSettings from "../ModalContent/ReportSectionSettings";
import CampaignActions from "./campaignActions";
import CampaignReportButtons from "./CampaignReportButtons";
import { COMBINED_REPORTS_WIDTH } from "./ReportStateButton";
import NewProductModal from "../ModalContent/NewProductModal";
import ReportFilter from "../ModalContent/ReportFilter/ReportFilter";
import { fetchReportSettings, resetShareModalInfo } from "../../../api_calls/ReportSettings";
import CampaignInternalNotes from "../ModalContent/CampaignInternalNotes";
import { fetchCampaignInternalNote, resetCampaignInternalNote } from "../../../api_calls/campaignInternalNotes";
import { checkSectionAvailability } from "../../../api_calls/WorkspaceSections";
import { useListingTableController } from "../../../hooks/useListingTableController";
import ListingDataTable from "../../../components/commonComponents/TabularLists/ListingDataTable";
import { ListingCellCenter, ListingCellLeft } from "../../../components/commonComponents/TabularLists/styles";
import RetailerIconCount from "../../../components/commonComponents/TabularLists/RetailerIconCount";
import { resetListingCheckData } from "../../../api_calls/listingCheck";
import { setBatchLoadingStatus } from "../../../api_calls/requestConfig";

const CampaignList = () => {
  const campaignDetails = useSelector((state) => state.userSelections);

  const navigate = useNavigate();
  const userRole = checkUserRole();
  const downloadDataOptions = {
    "Impressions Data": 1,
    "Conversion Data - Stores": 3,
    "Conversion Data - Total": 2,
    "All Campaign Data": 4,
  };
  const ReportSettingOptions = ["Report Settings", "Data Sanity"];

  let listItems =
    typeof campaignDetails.campaign_list !== "undefined"
      ? campaignDetails.campaign_list.all_campaigns
      : [];
  let campaignListitems = listItems.length !== 0 ? listItems : [];

  const isBrandRole =
    typeof userRole.user_role !== "undefined" && userRole.user_role.toLowerCase() === "brand";
  const sectionAvailability = campaignDetails.report_section_availability;

  // Performance sections are tenant-wide (single boolean from availability API).
  // Brand users always see performance as available; others use the Redux flag.
  const performanceAvailable = isBrandRole
    ? true
    : sectionAvailability?.performance_section === true;

  // Impression sections are per root retailer. Brand users always available;
  // otherwise look up this campaign's root_retailer_company.id in the map.
  const isImpressionSectionAvailable = (campaign) => {
    if (isBrandRole) {
      return true;
    }

    const rootRetailerId = campaign?.root_retailer_company?.id;
    if (typeof rootRetailerId === "undefined" || rootRetailerId === null) {
      return false;
    }

    return sectionAvailability?.impression_sections?.[rootRetailerId] === true;
  };

  const checkRequestAccess = pageAccessAllowed(["campaign_reports_performance_can_request"], []);
  const checkEditAccess = pageAccessAllowed(["campaign_products_can_edit", "campaign_activities_can_edit", "campaign_list_can_edit", "campaign_can_share", "campaign_can_download", "campaign_reports_settings_can_view"], []);
  const isActionColumnAccessible = checkEditAccess;

  const checkUserDetails = checkUserDetailsFromCookie();
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;

  let userDetailObj = {};
  if (checkUserDetails.success === true) {
    userDetailObj = checkUserDetails.user_detail;
  }
  const [userInfo, setUserInfo] = useState(userDetailObj);
  const [popoverOpen, setPopoverOpen] = useState(null);
  const [options, setOptions] = useState([]);
  const [downloadOptions, setDownloadOptions] = useState(false);
  const [reportSettingsOptions, setReportSettingsOptions] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);

  const [openModal, setOpenModal] = useState({
    products: false,
    activities: false,
    activity_list: false,
    data: false,
    edit_campaign: false,
    product_list: false,
    share_report: false,
  });
  const [campaignInfo, setCampaignInfo] = useState({});

  // to display the action items in popover when an icon is clicked.
  const handlePopoverOpen = (event, newOptions) => {
    setOptions(newOptions);
    setPopoverOpen(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPopoverOpen(null);
    setDownloadOptions(false);
    setReportSettingsOptions(false);
  };

  // to handle the action when a specific icon is click from the action list.
  const handleOptionClick = (option) => {
    if (option === "Add new product") {
      setOpenModal({
        ...openModal,
        products: true,
      });
    }

    if (option === "Add new activity" || option === "List activities") {
      setBatchLoadingStatus(true);
      fetchRequiredDetailsBeforePopup(campaignInfo, navigate).then((info) => {
        setTimeout(() => {
          setOpenModal({
            ...openModal,
            activity_list: option === "List activities" ? true : false,
            activities: option === "Add new activity" ? true : false,
          });
          setBatchLoadingStatus(false);
        }, 1000);
      });
    }

    if (option === "List products") {
      setOpenModal({
        ...openModal,
        product_list: true,
      });
    }

    if (option === "Request Report") {
    }
    // download data option click
    if (Object.keys(downloadDataOptions).includes(option)) {
      downloadCampaignData(campaignInfo.campaign_name, downloadDataOptions[option]);
    }

    // report settings option click
    if (ReportSettingOptions.includes(option)) {
      if (option === "Data Sanity") {
        navigate("/check-campaigns/data-sanity/" + campaignInfo.campaign_id);
      } else {
        setBatchLoadingStatus(true);
        fetchReportSettings(campaignInfo.campaign_id).then(() => {
          setOpenModal((prevState) => ({ ...prevState, report_settings: true }));
          setCampaignInfo(campaignInfo);
        }).finally(() => {
          setBatchLoadingStatus(false);
        });
      }
    }
    handlePopoverClose();
  };

  // Report Settings / Internal Notes / Data Sanity from the Action submenu (campaign is passed in so we do not wait on setState)
  const handleInternalSettingSelect = React.useCallback((option, selectedCampaign) => {
    setCampaignInfo(selectedCampaign);

    if (option === "Data Sanity") {
      navigate("/check-campaigns/data-sanity/" + selectedCampaign.campaign_id);
      return;
    }

    if (option === "Internal Notes") {
      setBatchLoadingStatus(true);
      // only open on a successful load, otherwise saving the empty editor could overwrite an existing note
      fetchCampaignInternalNote(selectedCampaign.campaign_id).then((info) => {
        if (info.success === true) {
          setOpenModal((prevState) => ({ ...prevState, internal_notes: true }));
        }
      }).finally(() => {
        setBatchLoadingStatus(false);
      });
      return;
    }

    setBatchLoadingStatus(true);
    fetchReportSettings(selectedCampaign.campaign_id).then(() => {
      setOpenModal((prevState) => ({ ...prevState, report_settings: true }));
    }).finally(() => {
      setBatchLoadingStatus(false);
    });
  }, [navigate]);

  // Hierarchy + section availability run in parallel with the campaign list request.
  // Page spinner is held by the listing controller until this AND the list request finish.
  const initCampaignLookups = React.useCallback(async () => {
    await Promise.all([
      // loadCampaigns(),
      // fetchCampaignNames(),
      // fetchOwnerNames("list-campaigns"),
      fetchCompanyHierarchy(),
      checkSectionAvailability(),
    ]);
    // scroll the page to the last position where user was before refreshing the page
    // scrollTableListings("campaign_pagination");
  }, []);

  const { pagination, onPageChange, onPageSizeChange } = useListingTableController({
    paginationReduxKey: "campaign_pagination",
    paginationCookieKey: "campaign_pagination",
    managePaginationCookie: manageCampaignPaginationCookie,
    loadPage: (pageNumber, pageSize) => loadCampaigns(pageNumber, pageSize).then(() => {
      setListLoaded(true);
    }),

    filtersReduxKey: "campaign_list_filters",
    filtersCookieKey: "campaign_list_filters",
    filterAppliedReduxKey: "campaign_list_filter_applied",

    onInit: initCampaignLookups,
    // onReset: resetCampaignsData,          // whatever your campaign reset fn is
    deletePaginationScope: "campaign",
    dataLoadedFlag: listLoaded,
    listingReloadKey: LISTING_RELOAD.campaigns,
  });

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: "name",
        header: "Name",
        cellClassName: "text-wrapper wrapLargeText",
        render: (campaign) => (
          <ListingCellLeft>
            <ThumbnailWithText>
              <ThumbnailText>{campaign.campaign_name}</ThumbnailText>
            </ThumbnailWithText>
          </ListingCellLeft>
        ),
      },
      {
        id: "progress",
        header: "Progress",
        headerAlign: "center",
        align: "center",
        render: (campaign) => (
          <ProgressBar
            startDate={campaign.campaign_start_date}
            endDate={campaign.campaign_end_date}
          />
        ),
      },
      {
        id: "owner",
        header: "Owner",
        headerAlign: "center",
        align: "center",
        render: (campaign) => (
          <ListingCellCenter>
            {campaign.owner_company_name}
          </ListingCellCenter>
        ),
      },
    ];

    // Retailer column only for cyreen / tenant (same user types as OwnerField)
    if (["cyreen", "tenant"].includes(userInfo.user_type)) {
      cols.push({
        id: "retailer",
        header: "Retailer",
        headerAlign: "center",
        align: "center",
        render: (campaign) => (
          <ListingCellCenter>
            <RetailerIconCount names={campaign.retailer_names} />
          </ListingCellCenter>
        ),
      });
    }

    // Reports sits before Action: Impressions + Performance in one cell
    const canSeeImpressions = permissionScope.campaign_reports_impression_can_view === true;
    const canSeePerformance = permissionScope.campaign_reports_performance_can_view === true;

    if (canSeeImpressions === true || canSeePerformance === true) {
      cols.push({
        id: "reports",
        header: "Reports",
        headerAlign: "center",
        align: "center",
        // Keep both report buttons on one line and the same width on every row
        cellSx: { minWidth: COMBINED_REPORTS_WIDTH + 16, whiteSpace: "nowrap" },
        render: (campaign) => (
          <CampaignReportButtons
            campaign={campaign}
            canSeeImpressions={canSeeImpressions}
            canSeePerformance={canSeePerformance}
            impressionSectionAvailable={isImpressionSectionAvailable(campaign)}
            performanceAvailable={performanceAvailable}
            canRequestPerformance={checkRequestAccess.success === true}
            userInfo={userInfo}
            userRole={userRole}
            navigate={navigate}
          />
        ),
      });
    }

    // Action column last — single gear menu (only if the user can use any action)
    if (isActionColumnAccessible.success === true) {
      cols.push({
        id: "action",
        hideWhenEmpty: true,
        header: "Action",
        headerAlign: "center",
        align: "center",
        render: (campaign) => (
          <CampaignActions
            campaign={campaign}
            checkEditAccess={checkEditAccess}
            userInfo={userInfo}
            setCampaignInfo={setCampaignInfo}
            setOpenModal={setOpenModal}
            fetchCampaign={fetchCampaign}
            onInternalSettingSelect={handleInternalSettingSelect}
          />
        ),
      });
    }

    return cols;
  }, [
    campaignDetails,
    userInfo,
    userRole,
    isActionColumnAccessible.success,
    performanceAvailable,
    permissionScope.campaign_reports_impression_can_view,
    permissionScope.campaign_reports_performance_can_view,
    handleInternalSettingSelect,
  ]);


  return (<Box>
    <ListingDataTable
      columns={columns}
      rows={campaignListitems}
      tableMinWidth={1100}
      rowKey={(c, idx) => `${c.campaign_id ?? idx}_campaign_row`}
      pagination={pagination}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      // Show table Loading… only for this list (first load + pagination/filters/save).
      // Page spinner still uses batchLoading; New Campaign must not blank the table.
      loading={listingTableIsLoading(campaignDetails, LISTING_RELOAD.campaigns, campaignDetails.campaign_list)}
    />

    {/* ---------- Load more button ----------- */}
    <Box height={20} />

    {/* ======== popover for the action items list =========== */}
    <Popover
      open={Boolean(popoverOpen)}
      anchorEl={popoverOpen}
      onClose={handlePopoverClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <List>
        {options.map((option, index) => (
          <div key={index + "_popoverOptions"}>
            {/* Download data Options */}
            {downloadOptions === true && (
              // some buttons will be disabled if performance report is not yet generated
              <ListItemButton
                disabled={
                  ![4, 5].includes(campaignInfo.campaign_status_code) &&
                    ["Conversion Data - Stores", "Conversion Data - Total"].includes(option)
                    ? true
                    : false
                }
                onClick={() => handleOptionClick(option)}
              >
                <ListItemText secondary={option} />
              </ListItemButton>
            )
            }

            {/* Report Settings Options */}
            {reportSettingsOptions === true && (
              <HtmlTooltipDisabled title={permissionScope.campaign_reports_settings_can_view === false &&
                ["Report Settings"].includes(option) ? "Not Available" : ""}>
                {/* tooltip will only be shown for disabled options */}
                <span>
                  <ListItemButton
                    disabled={
                      permissionScope.campaign_reports_settings_can_view === false &&
                        ["Report Settings"].includes(option)
                        ? true
                        : false
                    }
                    onClick={() => handleOptionClick(option)}
                  >
                    <ListItemText secondary={option} />
                  </ListItemButton>
                </span>
              </HtmlTooltipDisabled>
            )}
          </div>
        ))}
      </List>
    </Popover>
    {/* -------- add products modal --------- */}
    {openModal.products === true && (
      <NewProductModal
        open={openModal.products}
        onClose={() => {
          setOpenModal({
            ...openModal,
            products: false,
          });
          /*
              reset the campaign sheet data (process of creating an adlevel will start all over again 
              if user comes back from the upload campaign sheet page and revisit the page for a 
              different campaign or even for same campaign.
            */
          resetCampaignSheetData();
          resetListingCheckData();
          loadCampaigns();
        }}
        size={"xl"}
        closeModal={() => {
          setOpenModal({
            ...openModal,
            products: false,
          });
          /*
              reset the campaign sheet data (process of creating an adlevel will start all over again 
              if user comes back from the upload campaign sheet page and revisit the page for a 
              different campaign or even for same campaign.
            */
          resetCampaignSheetData();
          resetListingCheckData();
          loadCampaigns();
        }}
        usedFor={"add"}
        campaignRecord={campaignInfo}
      />
    )}

    {/* ------- marketing activities list --------- */}
    {openModal.activity_list === true && (
      <MarketingActivitiesList
        open={openModal.activity_list}
        onClose={() => {
          setOpenModal({
            ...openModal,
            activity_list: false,
          });
          resetMarketingActivitiesData();
        }}
        size={"xl"}
        closeModal={() => {
          setOpenModal({
            ...openModal,
            activity_list: false,
          });
          resetMarketingActivitiesData();
        }}
        campaignRecord={campaignInfo}
      />
    )}
    {/* ----------- marketing activities popup --------- */}
    {openModal.activities === true && (
      <AddNewMarketingActivity
        open={openModal.activities}
        onClose={() => {
          setOpenModal({
            ...openModal,
            activities: false,
          });
          resetMarketingActivitiesData();
        }}
        size={"md"}
        closeModal={() => {
          setOpenModal({
            ...openModal,
            activities: false,
          });
          resetMarketingActivitiesData();
        }}
        usedFor={"add"}
        campaignRecord={campaignInfo}
      />
    )}
    {/* --------- edit campaign modal --------- */}
    {openModal.edit_campaign === true && (
      <AddNewCampaign
        open={openModal.edit_campaign}
        onClose={() => {
          setOpenModal({
            ...openModal,
            edit_campaign: false,
          });
          resetCampaignSheetData();
        }}
        size={"md"}
        closeModal={() => {
          setOpenModal({
            ...openModal,
            edit_campaign: false,
          });
          resetCampaignSheetData();
        }}
        usedFor={"update"}
        campaignRecord={campaignInfo}
      />
    )}
    {/* ------- share report modal --------- */}
    {openModal.share_report === true && (
      <ReportFilter
        open={openModal.share_report}
        onClose={() => {
          setOpenModal({
            ...openModal,
            share_report: false,
          });
          resetShareModalInfo();
        }}
        size={"md"}
        closeModal={() => {
          setOpenModal({
            ...openModal,
            share_report: false,
          });
          resetShareModalInfo();
        }}
        usedFor={"update"}
        campaignRecord={campaignInfo}
      />
    )}

    {/* ------- report section modal ---------- */}
    {openModal.report_settings === true && (
      <ReportSectionSettings
        open={openModal.report_settings}
        onClose={() => {
          setOpenModal({
            ...openModal,
            report_settings: false,
          });
        }}
        size={"md"}
        closeModal={() => {
          setOpenModal({
            ...openModal,
            report_settings: false,
          });
        }}
        usedFor={"update"}
        campaignRecord={campaignInfo}
      />
    )}

    {/* ------- internal campaign notes modal ---------- */}
    {openModal.internal_notes === true && (
      <CampaignInternalNotes
        open={openModal.internal_notes}
        size={"md"}
        closeModal={() => {
          setOpenModal((prevState) => ({
            ...prevState,
            internal_notes: false,
          }));
          resetCampaignInternalNote();
        }}
        campaignRecord={campaignInfo}
      />
    )}
  </Box>);
};

export default CampaignList;
